import React from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import useCrearOt from '../../components/Ot/CrearOt/useCrearOt';
import NumeroOrdenInput from '../../components/Ot/CrearOt/NumeroOrdenInput';
import ObservacionInput from '../../components/Ot/CrearOt/ObservacionInput';
import ClienteSelector from '../../components/Ot/CrearOt/ClienteSelector';
import SedeSelector from '../../components/Ot/CrearOt/SedeSelector';
import EquipoManager from '../../components/Ot/CrearOt/EquipoManager';
import ListaEquiposConfirmacion from '../../components/Ot/CrearOt/ListaEquiposConfirmacion';
import ModalEditarSede from '../../components/Ot/CrearOt/ModalEditarSede';
import ModalDuplicados from '../../components/Ot/CrearOt/ModalDuplicados';
import ModalResolverItems from '../../components/Ot/CrearOt/ModalResolverItems';
import ModalConfirmarDependientes from '../../components/Ot/CrearOt/ModalConfirmarDependientes';
import ClienteModal from '../../components/Clientes/ClienteModal';
import PropuestaComercialSearchSelect from '../../utils/PropuestaComercialSearchSelect';

/**
 * CrearOt - Main view for creating work orders
 * Orchestrates all child components using useCrearOt hook
 * @returns {JSX.Element}
 */
const CrearOt = () => {
    
    const {
        // State
        cliente,
        sede, 
        equipos,
        numeroOrden,
        observacion,
        propuestaComercial,
        listaSedes,
        loading,

        // Modal states
        modalCrearCliente,
        modalEditarSede,
        modalCrearSede,
        modalDuplicados,
        modalResolverItems,
        modalDependientes,

        // Handlers
        setNumeroOrden,
        setObservacion,
        handleClienteChange,
        handlePropuestaChange,
        handleCrearCliente,
        handleSubmitCrearCliente,
        handleSedeChange,
        handleEditarSede,
        handleCrearSede,
        handleSubmitCrearSede,
        handleSubmitEditarSede,
        validarDuplicados,
        handleResolverItems,
        handleResolverDependientes,
        agregarEquipos,
        eliminarEquipo,
        editarEquipo,
        corregirEquipo,
        handleCrearOt,

        // Modal handlers
        closeModalCrearCliente,
        closeModalEditarSede,
        closeModalCrearSede,
        closeModalDuplicados,
        closeModalResolverItems
    } = useCrearOt();

     const isCrearDisabled = equipos.length === 0 || !numeroOrden.trim() || loading.creacion;

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>Crear Orden de Trabajo</h2>
                </Col>
            </Row>
            <Row>
                <Col lg={12}>
                    <Card className="mb-4 w-100">
                        <Card.Body className="w-100">
                            <h5 className="mb-3">Información Básica</h5>
                            <Row>
                                <Col lg={8}>
                                    <PropuestaComercialSearchSelect
                                        value={propuestaComercial}
                                        onChange={handlePropuestaChange}
                                        menuPortalTarget={document.body}
                                    />
                                </Col>
                            </Row>
                            <Row>
                                <Col lg={8} >
                                    <ClienteSelector
                                        value={cliente}
                                        onChange={handleClienteChange}
                                        onCrearCliente={handleCrearCliente}
                                        disabled={!!propuestaComercial}
                                    />
                                </Col>
                                <Col md={6} lg={4} >
                                    <NumeroOrdenInput
                                        value={numeroOrden}
                                        onChange={(e) => setNumeroOrden(e.target.value)}
                                        disabled={!!propuestaComercial}
                                    />
                                </Col>
                            </Row>
                            <SedeSelector
                                listaSedes={listaSedes}
                                value={sede}
                                onChange={handleSedeChange}
                                onEditar={handleEditarSede}
                                onCrearSede={handleCrearSede}
                                loading={loading.sedes}
                                clienteId={cliente?._id}
                            />

                            <ObservacionInput
                                value={observacion}
                                onChange={(e) => setObservacion(e.target.value)}
                            />
                            <div className="d-grid">
                                <Button
                                    variant="success"
                                    size="lg"
                                    onClick={handleCrearOt}
                                    disabled={isCrearDisabled}
                                >
                                    {loading.creacion ? 'Creando OT...' : 'Crear Orden de Trabajo'}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Col lg={12}>
                    <Card className="mb-3 w-100" style={{ position: 'sticky', top: '20px' }}>
                        <Card.Body className="w-100">
                            <ListaEquiposConfirmacion
                                equipos={equipos}
                                onEliminar={eliminarEquipo}
                                onEditar={editarEquipo}
                                listaSedes={listaSedes}
                                onCorregir={corregirEquipo}
                            />
                            {isCrearDisabled && equipos.length === 0 && (
                                <div className="text-muted text-center mt-2 small">
                                    Agregue al menos un equipo para crear la OT
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
            <Row>
                <Card className="w-100">
                    <Card.Body className="w-100">
                        <EquipoManager
                            clienteId={cliente?._id}
                            sedeId={sede?._id}
                            onValidarDuplicados={validarDuplicados}
                            onResolverItems={handleResolverItems}
                            onResolverDependientes={handleResolverDependientes}
                            onAgregar={agregarEquipos}
                            equiposYaAgregados={equipos}
                            disabled={!cliente || !sede}
                            listaSedes={listaSedes}
                            onCorregir={corregirEquipo}
                        />
                    </Card.Body>
                </Card>
                
            </Row>

            {/* Modals */}
            {modalCrearCliente.show && (
                <ClienteModal
                    show={modalCrearCliente.show}
                    onHide={closeModalCrearCliente}
                    onSave={handleSubmitCrearCliente}
                    usuario={null}
                    loading={false}
                />
            )}

            {modalEditarSede.show && (
                <ModalEditarSede
                    show={modalEditarSede.show}
                    onHide={closeModalEditarSede}
                    sede={modalEditarSede.data}
                    clienteId={cliente?._id}
                    onSubmit={handleSubmitEditarSede}
                    loading={false}
                />
            )}

            {modalCrearSede.show && (
                <ModalEditarSede
                    show={modalCrearSede.show}
                    onHide={closeModalCrearSede}
                    sede={null}
                    clienteId={cliente?._id}
                    onSubmit={handleSubmitCrearSede}
                    loading={false}
                    mode="create"
                    initialData={{
                        departamento: cliente?.departamento || '',
                        ciudad: cliente?.ciudad || '',
                        contSede: cliente?.contacto || '',
                        celContacto: cliente?.telefono || '',
                        emailContacto: cliente?.correo || ''
                    }}
                />
            )}

            {modalDuplicados.show && (
                <ModalDuplicados
                    show={modalDuplicados.show}
                    onHide={modalDuplicados.onHide}
                    data={modalDuplicados.data}
                    equiposYaAgregados={modalDuplicados.equiposYaAgregados || []}
                    onSelect={modalDuplicados.onSelect}
                />
            )}

            {modalResolverItems.show && (
                <ModalResolverItems
                    show={modalResolverItems.show}
                    onHide={modalResolverItems.onHide}
                    equiposSinItem={modalResolverItems.data}
                    onResolve={modalResolverItems.onResolve}
                />
            )}

            {modalDependientes.show && (
                <ModalConfirmarDependientes
                    show={modalDependientes.show}
                    grupos={modalDependientes.grupos}
                    onIncluirTodos={modalDependientes.onIncluirTodos}
                    onSoloSeleccionados={modalDependientes.onSoloSeleccionados}
                    onCancelar={modalDependientes.onCancelar}
                />
            )}
        </Container>
    );
};

export default CrearOt;
